import * as Yup from 'yup';
import { Box } from '@material-ui/core';
import { Formik } from 'formik';
import { SBase, SETVIImage } from '@setvi/shared/components';
import { Product as ProductDTO } from '@setvi/shared/interfaces';
import { FC } from 'react';
import { useQuoteConfig } from 'pages/quotes/hooks/use-quote-config';
import { SPOFormFields } from './spo-form-fields';

const validationSchema = Yup.object({
  name: Yup.string()
    .trim()
    .max(100, 'Limit is 100 characters')
    .required('Product name is required'),
  vendorName: Yup.string()
    .max(500, 'Limit is 500 characters')
    .required('Vendor name is required'),
  sku: Yup.string()
    .max(100, 'Limit is 100 characters')
    .required('SKU is required'),
  price: Yup.number()
    .required('Price is required')
    .min(1, 'Price must be greater than 0')
});

interface SPOFormValues {
  name: string;
  vendorName: string;
  sku: string;
  price: number | null;
}

interface AddSPOProps {
  onClose: () => void;
  onSubmit: (product: Partial<ProductDTO>) => Promise<any>;
  isAddingProducts: boolean;
}

export const AddSPO: FC<AddSPOProps> = ({
  onClose,
  onSubmit,
  isAddingProducts
}) => {
  const { quoteConfig } = useQuoteConfig({});

  const handleSubmit = async (value: SPOFormValues) => {
    const profitMarginPercentage = quoteConfig?.marginPercentage || 0;

    const cost = Number(value?.price);
    const margin = Number(profitMarginPercentage);
    const sellPerUnit = cost / (1 - margin / 100);
    const totalGrossProfit = sellPerUnit - cost;

    const product = {
      name: value.name,
      vendor: {
        name: value.vendorName || '',
        _id: null as string | null,
        vendorId: null as string | null
      },
      sku: value.sku,
      cost,
      sell: sellPerUnit,
      isFreeHand: true,
      isEditable: true,
      quantity: 1,
      totalGrossProfit,
      totalGrossProfitPercentage: profitMarginPercentage,
      isPricingFetching: false
    };

    await onSubmit(product);
  };

  return (
    <Formik
      initialValues={{ name: '', vendorName: '', sku: '', price: null }}
      validationSchema={validationSchema}
      onSubmit={async (values, { resetForm }) => {
        await handleSubmit(values);
        onClose?.();
        resetForm();
      }}>
      {formik => (
        <SBase
          header={{ title: 'Add SPO', onClose }}
          footer={{
            onConfirm: () => formik.handleSubmit(),
            onClose,
            confirmLabel: 'Add Product',
            closeLabel: 'Cancel',
            disableConfirm:
              !formik.isValid || !formik.dirty || isAddingProducts,
            confirmLoading: isAddingProducts
          }}
          width={1024}
          height={768}>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            flex={1}
            style={{ gap: 32 }}>
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              flex={1}>
              <SETVIImage
                width="80%"
                src="/images/percentage-spo-illustration.png"
                alt="Add SPO"
              />
            </Box>
            <Box flex={1}>
              <SPOFormFields formik={formik} />
            </Box>
          </Box>
        </SBase>
      )}
    </Formik>
  );
};
